import React, { useState, useEffect } from "react";
import TableComponent from "../../TableComponent/TableComponent";
import IconButton from "../../IconButton/IconButton";
import ModalComponent from "../../ModalComponent/ModalComponent";
import PlusIcon from "../../../../assets/PlusIcon";
import "./SlotManagement.css";
import ViewSlot from "./ViewSlot";
import { getAllSlots, createNewSlot, updateSlot, getSlot, getPublishedSlotDetails } from "../../../../services/svt/svt-admin/slot-management.service";
import Loader from "../../../common/Loader/Loader";
import validateFields from '../../../../utils/ValidationInput';
import { DataTableComponent } from "../../../common/TableComponent/DataTableComponent";
import { formatDate } from "../../../../utils/dateFormat";
import { Tooltip } from "react-tooltip";
import { toast } from "react-toastify";
import { useTranslation } from "react-i18next";

const newSlotData = [
    { fieldName: 'Slot', name: "name", fieldValue: '', type: 'input' },
    { fieldName: 'Start Date', name: "startDate", fieldValue: '', type: 'datepicker' },
    { fieldName: 'End Date', name: "endDate", fieldValue: '', type: 'datepicker' },
    {
        fieldName: 'Status', name: "status", fieldValue: '', type: 'dropdown', options: [{ value: "active", label: "Active" },
        { value: "deactive", label: "De-active" }]
    },
    { fieldName: 'Comments', name: "comments", fieldValue: '', type: 'textarea' }
];

const SlotManagement = () => {
    const [loading, setLoading] = useState(true);
    const { t } = useTranslation();
    const [isModalOpen, setIsModalOpen] = useState(false);
    const [slotData, setSlotData] = useState(newSlotData);
    const [tableData, setTableData] = useState([]);
    const [modalType, setModalType] = useState('');
    const [selectedRowIndex, setSelectedRowIndex] = useState('');
    const [showSlotDetailsPage, setShowSlotDetailsPage] = useState(false);
    const [viewSlotData, setViewSlotData] = useState('');
    const [slotDetails, setSlotDetails] = useState('');
    const [publishedSlotsData, setPublishedSlotsData] = useState([]);
    const [selectedStatus, setSelectedStatus] = useState('');

    const columns = [
        { name: t("slotName"), field: "name", selector: row => row.name, sortable: true, wrap: true },
        { name: t("status"), field: 'status', selector: row => row.status, sortable: true },
        {
            name: t("slotDuration"),
            field: "slotDuration",
            selector: row => row.slotDuration,
            sortable: true,
            sortFunction: (a, b) => {
                // Sort by startDate (ISO string or Date)
                const aDate = a.startDate ? new Date(a.startDate) : new Date(0);
                const bDate = b.startDate ? new Date(b.startDate) : new Date(0);
                return aDate - bDate;
            }
        },
        { name: t("modifiedDate"), field: "modifiedDate", selector: row => row.modifiedDate, sortable: true },
        { name: t("createdBy"), field: "createdBy", selector: row => row.createdBy, sortable: true },
        { name: t("comments"), field: "comments", selector: row => row.comments, sortable: true },
    ];

    const [formData, setFormData] = useState({
        name: '',
        startDate: '',
        endDate: '',
        comments: '',
        status: 'active'
    });

    const getSlotData = () => ([
        { fieldName: t('slotName'), name: "name", fieldValue: formData.name || '', type: 'input', required: true },
        { fieldName: t('startDate'), name: "startDate", fieldValue: formData.startDate || '', type: 'datepicker', required: true },
        { fieldName: t('endDate'), name: "endDate", fieldValue: formData.endDate || '', type: 'datepicker', required: true },
        {
            fieldName: t('status'), name: "status", fieldValue: formData.status || '', type: 'dropdown', options: [
                { value: "active", label: t("active") },
                { value: "deactive", label: t("deactive") }
            ],
            isFieldDisabled: (modalType === 'edit' && publishedSlotsData.length !== 0 && selectedStatus === 'active') ? true : false
        },
        { fieldName: t('comments'), name: "comments", fieldValue: formData.comments || '', type: 'textarea' }
    ]);

    useEffect(() => {
        fetchData();
    }, []);

    // Fetch all slots
    const fetchData = async () => {
        try {
            setLoading(true);
            const slotsResult = await getAllSlots();
            const updatedData = slotsResult?.data?.map(item => ({
                ...item,
                status: item.isActive === true ? 'Active' : 'De-Active',
            }));
            setTableData(updatedData);
        } catch (error) {
            console.error("Error fetching data:", error);
        } finally {
            setLoading(false);
        }
    };

    const closeModal = () => {
        setIsModalOpen(false);
        setFormData({
            name: '',
            startDate: '',
            endDate: '',
            comments: '',
            status: 'active'
        })
    }

    const onAddNewSlot = () => {
        setIsModalOpen(true);
        setModalType('add');
    };

    const onChangeInput = (inputData) => {
        const { name, value } = inputData;

        toast.dismiss();
        if (name === "name" && value.length > 30) {
            toast.error(t("slotNameExceed"), { position: "top-center" });
            return;
        }
        if (name === "comments" && value.length > 255) {
            toast.error(t("commentsCannotExceed", { number: 255 }), { position: "top-center" });
            return;
        }

        setFormData((prevData) => ({
            ...prevData,
            [name]: value
        }));
    };

    const onChangeDate = (date, name) => {
        if (date !== null) {
            const newStartDate = new Date(date);
            newStartDate.setHours(0, 0, 0, 0);
            const nextYearDate = new Date(newStartDate);
            nextYearDate.setFullYear(newStartDate.getFullYear() + 1);

            const newNextYearDate = new Date(nextYearDate);
            newNextYearDate.setDate(newNextYearDate.getDate() - 1); // Subtract one day

            if (name === 'startDate') {
                setFormData((prevData) => ({
                    ...prevData,
                    // 'endDate': convertToISOString(newNextYearDate),
                    'startDate': convertToISOString(newStartDate)
                }));
            } else {
                setFormData((prevData) => ({
                    ...prevData,
                    [name]: convertToISOString(newStartDate)
                }));
            }
        } else {
            setFormData((prevData) => ({
                ...prevData,
                [name]: null,
            }));
        }
    };

    const convertToISOString = (date) => {
        const offset = date?.getTimezoneOffset();
        const adjustedDate = new Date(date?.getTime() - offset * 60000);
        return adjustedDate.toISOString();
    };

    const trimText = (text) => {
        return text.trim().replace(/\s+/g, ' ');
    };

    const saveData = async () => {
        setLoading(true);

        // Required fields with types (text or email)
        const requiredFields = [
            { label: t("slotName"), key: "Slot Name", type: "text", value: trimText(formData.name) },
            { label: t("startDate"), key: "Start Date", type: "date", value: formData.startDate },
            { label: t("endDate"), key: "End Date", type: "date", value: formData.endDate },
            { label: t("comments"), key: "Comments", type: "text", value: formData.comments, charLength: 255 },
        ];
        const isValid = validateFields(formData, requiredFields, 'byValue');
        if (!isValid) {
            setLoading(false);
            return;
        }

        let payload = {
            slotName: trimText(formData.name),
            startDate: formData.startDate,
            endDate: formData.endDate,
            comments: formData.comments,
            status: formData.status === 'active' ? true : false
        }
        await createNewSlot(payload).then((response) => {
            if (response === null) {
                setLoading(false);
            } else {
                fetchData();
                closeModal();
                setLoading(false);
            }
        })
    };

    const updateData = async () => {
        setLoading(true);

        // Required fields with types (text or email)
        const requiredFields = [
            { label: t("slotName"), key: "Slot Name", type: "text", value: trimText(formData.name) },
            { label: t("startDate"), key: "Start Date", type: "date", value: formData.startDate },
            { label: t("endDate"), key: "End Date", type: "date", value: formData.endDate },
            { label: t("comments"), key: "Comments", type: "text", value: formData.comments, charLength: 255 },
        ];
        const isValid = validateFields(formData, requiredFields, 'byValue');
        if (!isValid) {
            setLoading(false);
            return;
        }

        let payload = {
            slotName: trimText(formData.name),
            startDate: formData.startDate,
            endDate: formData.endDate,
            comments: formData.comments,
            isActive: formData.status === 'active' ? true : false,
        }
        let slotId = formData.id;
        await updateSlot(payload, slotId).then((response) => {
            if (response === null) {
                setLoading(false);
            } else {
                fetchData();
                closeModal();
                setLoading(false);
            }
        })
    };

    const onEditDetails = (data, selectedIndex) => {
        setModalType('edit');
        setIsModalOpen(true);
        setSelectedRowIndex(selectedIndex);
        let { name, startDate, endDate, comments, id, status } = data;
        setFormData({
            name: name,
            startDate: startDate,
            endDate: endDate,
            comments: comments,
            id: id,
            status: status === 'Active' ? 'active' : 'deactive',
        })
        setSelectedStatus(status === 'Active' ? 'active' : 'deactive');
        fetchPublishedSlotsInEdit(data);
    };

    const fetchPublishedSlotsInEdit = async (data) => {
        try {
            setLoading(true);
            let payload = {
                id: data.id
            }
            const slotsResult = await getPublishedSlotDetails(payload);
            setPublishedSlotsData(slotsResult?.data);
        } catch (error) {
            console.error("Error fetching data:", error);
        } finally {
            setLoading(false);
        }
    }

    const fetchPublishedSlotsData = async (data) => {
        try {
            setLoading(true);
            let payload = {
                id: data.id
            }
            const slotsResult = await getPublishedSlotDetails(payload);
            const updatedData = slotsResult?.data?.map(item => ({
                ...item,
                status: item.isActive === true ? 'Active' : 'De-Active',
            }));

            setViewSlotData(updatedData);
            setSlotDetails(data);
        } catch (error) {
            console.error("Error fetching data:", error);
        } finally {
            setLoading(false);
        }
    };

    const onViewSlotDetails = (data, selectedIndex) => {
        // Show View slot page
        setShowSlotDetailsPage(true)
        fetchPublishedSlotsData(data);
    };

    const onCloseViewSlot = () => {
        setShowSlotDetailsPage(false)
    }

    return (
        <>
            {loading ? <Loader /> :
                <div className={["glbOuterWrapper", "slotMgmt"].join(' ')}>
                    {!showSlotDetailsPage ?
                        <>
                            {/* <div className="buttonOuterDiv">
                                <IconButton
                                    text="New Slot"
                                    onClick={onAddNewSlot}
                                    style={{ backgroundColor: "#F0F0F0", color: '#454647', border: '1px solid #B3B2B4' }}
                                    icon={<PlusIcon />}
                                />
                            </div> */}
                            <div className="surveyHeader">
                                <h2 className="surveyTitle">{t("listOfSlots")}</h2>
                                <IconButton
                                    text={t("newSlot")}
                                    onClick={onAddNewSlot}
                                    style={{
                                        backgroundColor: "#F0F0F0",
                                        color: "#454647",
                                        border: "1px solid #B3B2B4",
                                    }}
                                    icon={<PlusIcon />}
                                />
                            </div>
                            <hr></hr>

                            <div style={{ marginTop: "1rem" }}>
                                <DataTableComponent
                                    columns={columns}
                                    data={tableData}
                                    onEditDetails={onEditDetails}
                                    tableName="slot"
                                    onViewSlotDetails={onViewSlotDetails}
                                    noDataMessage={t('noDataFound')}
                                    pageCount={5}
                                />
                            </div>

                            <ModalComponent
                                modalTitle={modalType === 'add' ? t("addNewSlot") : t("updateSlot")}
                                isOpen={isModalOpen}
                                fieldsData={getSlotData()}
                                formData={formData}
                                modalType={modalType}
                                onClose={closeModal}
                                onChangeInput={onChangeInput}
                                onChangeDate={onChangeDate}
                                buttons={[
                                    <button className="saveButton" onClick={modalType === 'add' ? saveData : updateData}>{modalType === 'add' ? t("add") : t("update")}</button>,
                                    <button className="cancelButton" onClick={closeModal}>{t("cancel")}</button>
                                ]}
                            />
                        </>
                        :
                        <ViewSlot onCloseViewSlot={onCloseViewSlot} viewSlotData={viewSlotData} slotDetails={slotDetails} />
                    }
                </div>
            }
        </>
    );
}

export default SlotManagement;
